import {
  getMenuTagDefinition,
  hydrateMenuTagRefs,
  resolveMenuItemLegalMarks,
} from '@web-studio/shared-schemas';
import type { MenuItem } from './parseMenuColumns';
import { pickChrome } from './siteChromeCopy';

export type MenuItemLegendTipGroup = {
  title: string;
  rows: string[];
};

/**
 * Allergens + additives + dietary for one dish — used as the name-row hover tip
 * so guests see the full Hinweise for that item in one place.
 */
export function buildMenuItemLegendTipGroups(
  item: MenuItem,
  locale: string,
  options?: { includeLegalMarks?: boolean },
): MenuItemLegendTipGroup[] {
  const includeLegalMarks = options?.includeLegalMarks !== false;
  const groups: MenuItemLegendTipGroup[] = [];

  if (includeLegalMarks) {
    const marks = resolveMenuItemLegalMarks(item.allergenIds, item.additiveIds, locale);
    const allergens = marks.filter((mark) => mark.kind === 'allergen');
    const additives = marks.filter((mark) => mark.kind === 'additive');
    if (allergens.length > 0) {
      groups.push({
        title: pickChrome('menuLegalAllergens', locale),
        rows: allergens.map((mark) => `${mark.code} · ${mark.label}`),
      });
    }
    if (additives.length > 0) {
      groups.push({
        title: pickChrome('menuLegalAdditives', locale),
        rows: additives.map((mark) => `${mark.code} · ${mark.label}`),
      });
    }
  }

  const dietary = hydrateMenuTagRefs(item.dietaryTagIds, locale).filter((tag) => {
    const def = getMenuTagDefinition(tag.id);
    return !def || def.kind === 'dietary';
  });
  if (dietary.length > 0) {
    groups.push({
      title: pickChrome('menuLegalDietary', locale),
      rows: dietary.map((tag) => tag.label),
    });
  }

  return groups;
}

/** Plain-text fallback for aria / native title (newline-separated sections). */
export function formatMenuItemLegendTipPlain(
  groups: readonly MenuItemLegendTipGroup[],
): string {
  return groups
    .map((group) => `${group.title}: ${group.rows.join(', ')}`)
    .join('\n');
}
